/**
 * API Response Transformation Utilities
 * 
 * Centralizes response structure handling for different API patterns.
 * When API routes change and response structures evolve, update these
 * transformers to maintain backward compatibility throughout the app.
 */

/**
 * Standard API response structure from select_query endpoints
 */
export interface SelectQueryResponse<T = unknown> {
  data: T[];
  info: {
    limit: number;
    results: number;
    total_results: number;
    elapsed_total: number;
    elapsed_per_item: number;
    db_query: string;
    db_query_parameters: Record<string, unknown>;
    select_query: boolean;
  };
}

/**
 * Standard API response structure for CRUD operations
 */
export interface StandardApiResponse<T = unknown> {
  data?: T;
  success?: boolean;
  failure?: boolean;
  message?: string;
  errors?: Array<{ message: string; code?: string }>;
}

/**
 * Transforms a select_query response to a standardized format
 * Use this when dealing with responses from select_query endpoints
 */
export const transformSelectQueryResponse = <T>(
  response: SelectQueryResponse<T>
): { data: T[]; total: number; info: SelectQueryResponse['info'] } => {
  return {
    data: response.data || [],
    total: response.info?.total_results || 0,
    info: response.info,
  };
};

/**
 * Extracts data array from select_query response
 * Use this for simple cases where you only need the data
 */
export const extractSelectQueryData = <T>(
  response: SelectQueryResponse<T>
): T[] => {
  return response.data || [];
};

/**
 * Extracts total count from select_query response
 */
export const extractSelectQueryTotal = (
  response: SelectQueryResponse
): number => {
  return response.info?.total_results || 0;
};

/**
 * Transforms a standard CRUD response
 * Use this for create/update/delete operations
 */
export const transformStandardResponse = <T>(
  response: StandardApiResponse<T>
): { data: T | null; success: boolean; message?: string | undefined } => {
  return {
    data: response.data || null,
    success: response.success ?? !response.failure,
    message: response.message,
  };
};

/**
 * Type guard to check if response is a select_query response
 */
export const isSelectQueryResponse = (
  response: unknown
): response is SelectQueryResponse => {
  if (typeof response !== 'object' || response === null || !('info' in response)) {
    return false;
  }
  const candidate = response as SelectQueryResponse;
  if ('data' in response) {
    return Array.isArray(candidate.data);
  }
  // Zero-row select_query responses arrive WITHOUT a `data` key at all: k3's
  // Jackson config suppresses empty lists globally (proven live on QA3 for both
  // this endpoint family and substitute-recommendation, ECOPD-17219/17300).
  // The `info.select_query` flag identifies the body precisely, so accept it and
  // let transformSelectQueryResponse fill in `data: []` — otherwise every caller
  // sees `data === undefined` the first time a query legitimately matches nothing.
  return candidate.info?.select_query === true;
};

/**
 * Type guard to check if response is a standard API response
 */
export const isStandardApiResponse = (
  response: unknown
): response is StandardApiResponse => {
  return (
    typeof response === 'object' &&
    response !== null &&
    ('success' in response || 'failure' in response || 'data' in response)
  );
};

/**
 * Generic response normalizer that handles both patterns
 * Use this when you're not sure which response pattern you'll receive
 */
export const normalizeApiResponse = <T>(
  response: SelectQueryResponse<T> | StandardApiResponse<T> | { data: T }
): { data: T | T[] | null; total?: number; success: boolean } => {
  if (isSelectQueryResponse(response)) {
    return {
      data: response.data || [],
      total: response.info?.total_results || 0,
      success: true,
    };
  }

  if (isStandardApiResponse(response)) {
    return {
      data: response.data || null,
      success: response.success ?? !response.failure,
    };
  }

  // Fallback for simple { data: T } responses
  return {
    data: (response as { data: T }).data || null,
    success: true,
  };
};

/**
 * Extracts the first item from a select_query response
 * Useful for queries that return a single record
 */
export const extractFirstSelectQueryItem = <T>(
  response: SelectQueryResponse<T>
): T | null => {
  const data = response.data || [];
  return data.length > 0 ? data[0] : null;
};

/**
 * Maps select_query results with a transform function
 * Useful for transforming array responses to objects
 */
export const mapSelectQueryResults = <TInput, TOutput>(
  response: SelectQueryResponse<TInput>,
  mapper: (item: TInput, index: number) => TOutput
): TOutput[] => {
  return (response.data || []).map(mapper);
};

/**
 * Creates a Map from select_query response using key extractor
 * Useful for creating lookup maps (e.g., id -> count)
 */
export const createMapFromSelectQuery = <T, K extends string | number>(
  response: SelectQueryResponse<T>,
  keyExtractor: (item: T) => K,
  valueExtractor: (item: T) => unknown
): Map<K, unknown> => {
  const map = new Map<K, unknown>();
  (response.data || []).forEach((item) => {
    const key = keyExtractor(item);
    const value = valueExtractor(item);
    map.set(key, value);
  });
  return map;
};